import { ReactFlow, Background, ReactFlowProvider, useReactFlow } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ChatbotNode } from './chatbot-node.component'
import { CustomEdge } from './custom-edge.component'
import { type TChatbotNode } from '../node.type'
import { useChatbotFlow } from '../chatbot.provider'
import { useTranslation } from 'react-i18next'
import { CurecallBadge, ECurecallBadgeColor, BackIcon, EArrowPosition } from '@honestica/curecall-ui'
import { CustomControls } from './custom-controls.component'
import { DashboardSubjectMissingBanner } from './dashboard-subject-missing-banner.component'
import { ChatbotPublishButton } from '../chatbot-publish-button.component'
import { ChatbotPreviewButton } from '../chatbot-preview/chatbot-preview-button.component'
import { ChatbotCopyButton } from '../chatbot-copy-button.component'
import { useEffect, useRef, useState } from 'react'
import { useCurrentProject } from 'src/providers/current-project.provider'
import Link from 'src/components/navigation/link.component'

const nodeTypes = {
  chatbotMessage: ChatbotNode,
} as const

const edgeTypes = {
  custom: CustomEdge,
} as const

const defaultEdgeOptions = {
  type: 'custom',
} as const

function NodeEditorContent() {
  const { fitView } = useReactFlow()
  const hasLoadedRef = useRef(false)
  const previousNodeCountRef = useRef(0)
  const { t } = useTranslation()
  const { project } = useCurrentProject()
  const {
    isChatbotPublished,
    hasUnsavedChanges,
    chatbotName,
    followupId,
    chatbotId,
    nodes,
    edges,
    isFormOpen,
    selectedNodeId,
    onNodesChange,
    onEdgesChange,
    onConnect,
    openFormForNode,
    copyNode,
    pasteNode,
    publishChatbot,
    unpublishChatbot,
  } = useChatbotFlow()

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement
      const tagName = target.tagName.toLowerCase()
      if (tagName === 'input' || tagName === 'textarea' || target.isContentEditable) return
      if (!event.metaKey && !event.ctrlKey) return

      if (event.key === 'c' && selectedNodeId) {
        event.preventDefault()
        copyNode(selectedNodeId)
      } else if (event.key === 'v') {
        event.preventDefault()
        pasteNode()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [copyNode, pasteNode, selectedNodeId])

  const FIT_VIEW_MAX_NODES = 10

  useEffect(() => {
    const currentNodeCount = nodes.length

    // Auto-center view once when nodes are loaded from API (more than initial nodes)
    if (currentNodeCount > 2 && !hasLoadedRef.current) {
      fitView({ padding: 0.2, duration: 300 })
      hasLoadedRef.current = true
      previousNodeCountRef.current = currentNodeCount
      return
    }

    // Auto-fit view when a node is added or removed (not just moved), only for small chatbots
    if (
      hasLoadedRef.current &&
      currentNodeCount !== previousNodeCountRef.current &&
      currentNodeCount <= FIT_VIEW_MAX_NODES
    ) {
      fitView({ padding: 0.2, duration: 300 })
    }

    previousNodeCountRef.current = currentNodeCount
  }, [nodes.length, fitView])

  useEffect(() => {
    // Auto-fit view when form opens or closes, only for small chatbots
    if (hasLoadedRef.current && nodes.length <= FIT_VIEW_MAX_NODES) {
      fitView({ padding: 0.2, duration: 300 })
    }
  }, [isFormOpen, fitView, nodes.length])

  const [isBadgeAnimating, setIsBadgeAnimating] = useState(false)

  useEffect(() => {
    if (!hasUnsavedChanges) return
    setIsBadgeAnimating(true)
    const timer = setTimeout(() => setIsBadgeAnimating(false), 1500)
    return () => clearTimeout(timer)
  }, [hasUnsavedChanges])

  const handleNodeClick = (_event: React.MouseEvent, node: TChatbotNode) => {
    openFormForNode(node.id)
  }

  return (
    <div className="c-h-full c-flex-1 c-flex c-flex-col">
      <h2 className="c-text-base c-font-bold c-mb-0 c-flex c-items-center c-gap-2">
        {t('pages.configuration.followups.chatbot.node-form.title', { chatbotName })}
        <CurecallBadge
          color={isChatbotPublished && hasUnsavedChanges ? ECurecallBadgeColor.RED : ECurecallBadgeColor.GREY}
          className={
            isChatbotPublished && hasUnsavedChanges && isBadgeAnimating
              ? 'c-h-max c-animate-badge-pulse'
              : 'c-h-max'
          }
        >
          {isChatbotPublished && hasUnsavedChanges
            ? t('pages.configuration.followups.chatbot.editor.unpublished-changes')
            : isChatbotPublished
              ? t('pages.configuration.followups.chatbot.published')
              : t('pages.configuration.followups.chatbot.unpublished')}
        </CurecallBadge>
      </h2>
      <div className="c-mb-2 c-flex c-gap-2">
        <ChatbotPublishButton
          isChatbotPublished={isChatbotPublished}
          hasUnsavedChanges={hasUnsavedChanges}
          onPublish={publishChatbot}
          onUnpublish={unpublishChatbot}
          followupId={followupId}
        />
        <ChatbotPreviewButton />
        <ChatbotCopyButton />
        <Link
          href={`/${project.id}/configuration/followups/${followupId}/chatbot/${chatbotId}/update-general-form`}
          className="c-flex c-items-center c-gap-0.5 c-text-sm c-font-semibold c-text-primary hover:c-text-primary-500 hover:c-underline c-border-l c-pl-2 c-border-grey-400"
        >
          <BackIcon position={EArrowPosition.TO_LEFT} className="c-h-4 c-w-4 c-mr-1" />
          {t('pages.configuration.followups.chatbot.node-form.back-to-step-1')}
        </Link>
      </div>
      <DashboardSubjectMissingBanner />
      <div className="c-flex-1 c-border c-border-gray-200 c-rounded-md c-bg-gray-50 c-relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={handleNodeClick}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          minZoom={0.2}
          maxZoom={1.5}
          connectionRadius={120}
        >
          <Background />
          <CustomControls />
        </ReactFlow>
      </div>
    </div>
  )
}

export function NodeEditor() {
  return (
    <ReactFlowProvider>
      <NodeEditorContent />
    </ReactFlowProvider>
  )
}
